import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import StageHeader from "./page/StageHeader";
import SubStepNav from "./page/SubStepNav";
import { useMsal } from "@azure/msal-react";
import {
  Table,
  Button,
  Tag,
  Tooltip,
  Popover,
  message,
  Modal,
  Empty,
  Select,
  Input,
  InputNumber,
  Upload,
  Space,
  Radio,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { apiConfig, apiTokenRequest } from "../authConfig";
import { useApp } from "../context/AppContext";
import AiProvenanceNote from "./AiProvenanceNote";
import { Role } from "../constants/roles";
import { getIncidentCta, getEvidenceCta } from "../utils/evidencingWorkflowCta";
import { CiDocxEditorPanel } from "./ControlImplementation";
import { stripCompanyPrefixFromDocumentTitle } from "../utils/documentDisplayName";
import { formatDdMmYyyy, formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";

const SUB_TABS = [
  { key: "incidents", label: "INCIDENTS" },
  { key: "evidence", label: "EVIDENCE" },
  { key: "dashboard", label: "DASHBOARD" },
];

const SEVERITY_OPTIONS = ["Low", "Medium", "High", "Critical"];
const SEVERITY_COLORS = {
  Low: "green",
  Medium: "gold",
  High: "orange",
  Critical: "red",
};

const STATUS_OPTIONS = ["Open", "In Progress", "Contained", "Closed"];

// Severity and status have no defaults: the user must choose both.
const EMPTY_NEW_ROW = { incidentId: "", title: "", link: "", severity: "", status: "", dateDetected: "", owner: "" };

const WORKFLOW_LABELS = {
  not_started: { label: "Not Started", color: "default", owner: "Lead Advisor" },
  pushed_to_spoc: { label: "SPOC Upload", color: "blue", owner: "Company SPOC" },
  delegate_review: { label: "Delegate Review", color: "purple", owner: "Company Delegate" },
  advisor_review: { label: "Advisor Review", color: "geekblue", owner: "Advisor" },
  spoc_final_approval: { label: "SPOC Final Approval", color: "orange", owner: "Company SPOC" },
  la_publish: { label: "Ready to Publish", color: "cyan", owner: "Lead Advisor" },
  published: { label: "Published", color: "green", owner: "-" },
};

const CSV_TEMPLATE_COLUMNS = [
  "Incident ID",
  "Title",
  "Link",
  "Severity",
  "Status",
  "Date Detected",
  "Owner",
];

/** Pending AI suggestion for an incident's impacted controls (Commandment IV): never shown as recorded until accepted. */
export function ImpactedControlsSuggestion({ row, canAccept, busy, onAccept }) {
  let suggestion = null;
  try {
    suggestion = row?.impactedControlsSuggestion ? JSON.parse(row.impactedControlsSuggestion) : null;
  } catch {
    suggestion = null;
  }
  const controls = Array.isArray(suggestion?.controls) ? suggestion.controls : [];
  if (controls.length === 0) return null;
  return (
    <div data-testid="impacted-controls-suggestion" className="rounded-lg border border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] p-2 space-y-1">
      <Tag color="purple" className="text-[10px]">AI suggestion, pending review</Tag>
      <div className="flex flex-wrap gap-0.5">
        {controls.map((c) => (
          <Tag key={c} className="text-[9px] px-1 py-0 leading-4">{c}</Tag>
        ))}
      </div>
      <AiProvenanceNote provenance={suggestion.provenance} testId="impacted-controls-provenance" />
      {canAccept ? (
        <Button size="small" type="primary" disabled={busy} onClick={() => onAccept(row.id)} data-testid="accept-impacted-controls">
          Accept suggestion
        </Button>
      ) : (
        <span className="text-[10px] text-[color:var(--color-text-muted)]">An advisor must accept this before it is recorded.</span>
      )}
    </div>
  );
}

function canEditInState(role, workflow) {
  if (role === Role.ADMIN) return true;
  switch (workflow) {
    case "pushed_to_spoc": return role === Role.COMPANY_SPOC;
    case "delegate_review": return role === Role.COMPANY_DELEGATE;
    case "advisor_review": return role === Role.ADVISOR || role === Role.LEAD_ADVISOR;
    case "spoc_final_approval": return role === Role.COMPANY_SPOC;
    case "la_publish": return role === Role.LEAD_ADVISOR;
    default: return false;
  }
}

export function EvidencingMonitoring({ project, user }) {
  const { instance } = useMsal();
  const { fetchProjectProfile, evidencingSubTabOverrideRef } = useApp();
  const [activeTab, setActiveTab] = useState("incidents");
  const [incidents, setIncidents] = useState([]);
  const [workflow, setWorkflow] = useState("not_started");
  const [currentVersion, setCurrentVersion] = useState(1);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [newRow, setNewRow] = useState(EMPTY_NEW_ROW);
  const [controlsMap, setControlsMap] = useState({});
  const loadedRef = useRef(false);

  const projectId = project?.id;
  const userRole = user?.role || "";

  useEffect(() => {
    const tab = evidencingSubTabOverrideRef?.current;
    if (tab === "incidents" || tab === "evidence" || tab === "dashboard") {
      setActiveTab(tab);
    }
    if (evidencingSubTabOverrideRef) evidencingSubTabOverrideRef.current = null;
  }, [project?.id, evidencingSubTabOverrideRef]);

  const getApiToken = useCallback(async () => {
    const resp = await instance.acquireTokenSilent({ ...apiTokenRequest, account: instance.getActiveAccount() });
    return resp.accessToken;
  }, [instance]);

  const fetchIncidents = useCallback(async (version) => {
    if (!projectId) return;
    try {
      const token = await getApiToken();
      const vParam = version ? `?version=${version}` : "";
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/incidents${vParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (j?.data) {
        setIncidents(j.data.incidents || []);
        setWorkflow(j.data.workflow || "not_started");
        if (j.data.currentVersion) setCurrentVersion(j.data.currentVersion);
      }
    } catch (e) {
      console.error("fetch incidents:", e);
    }
  }, [projectId, getApiToken]);

  const fetchControlsMap = useCallback(async () => {
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/iso27001/controls?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      const controls = j?.data?.controls || j?.data || [];
      if (Array.isArray(controls)) {
        const map = {};
        for (const c of controls) {
          if (c.ctrlNumber) map[c.ctrlNumber] = c.ctrlName || c.ctrlNumber;
        }
        setControlsMap(map);
      }
    } catch (e) {
      console.error("fetch controls map:", e);
    }
  }, [getApiToken]);

  useEffect(() => {
    if (!projectId || loadedRef.current) return;
    loadedRef.current = true;
    fetchIncidents(null);
    fetchControlsMap();
  }, [projectId, fetchIncidents, fetchControlsMap]);

  const canEdit = canEditInState(userRole, workflow);
  const cta = getIncidentCta(userRole, workflow);
  const wfMeta = WORKFLOW_LABELS[workflow] || WORKFLOW_LABELS.not_started;

  const handleUploadCsv = useCallback(async (file) => {
    if (!projectId) return false;
    setBusy(true);
    try {
      const token = await getApiToken();
      const formData = new FormData();
      formData.append("file", file);
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/incidents/upload-csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const j = await r.json();
      if (r.ok && j?.data) {
        setIncidents(j.data.incidents || []);
        setCsvResult({ inserted: j.data.inserted ?? 0, rejected: Array.isArray(j.data.rejected) ? j.data.rejected : [], error: null });
      } else if (Array.isArray(j?.error?.details?.rejected)) {
        // Every row was rejected: nothing was imported.
        setCsvResult({ inserted: 0, rejected: j.error.details.rejected, error: j?.error?.message || "No rows were imported." });
      } else {
        message.error(j?.error?.message || "Upload failed");
      }
    } catch (e) {
      message.error("Upload failed");
    } finally {
      setBusy(false);
    }
    return false;
  }, [projectId, getApiToken]);

  const handleAddRow = useCallback(async () => {
    if (!projectId || !newRow.incidentId.trim() || !newRow.title.trim()) {
      message.warning("Incident ID and Title are required.");
      return;
    }
    if (!newRow.severity || !newRow.status) {
      message.warning("Choose a severity and a status.");
      return;
    }
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/incidents/add-row`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(newRow),
      });
      const j = await r.json();
      if (r.ok && j?.data?.incident) {
        setIncidents((prev) => [...prev, j.data.incident]);
        setAddModalOpen(false);
        setNewRow(EMPTY_NEW_ROW);
        message.success("Row added");
      } else {
        message.error(j?.error?.message || "Add failed");
      }
    } catch (e) {
      message.error("Add failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, newRow, getApiToken]);

  const handleEditRow = useCallback(async () => {
    if (!projectId || !editRow) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/incidents/${editRow.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          incidentId: editRow.incidentId,
          title: editRow.title,
          link: editRow.link,
          severity: editRow.severity,
          status: editRow.status,
          dateDetected: editRow.dateDetected,
          owner: editRow.owner,
        }),
      });
      const j = await r.json();
      if (r.ok && j?.data?.incident) {
        setIncidents((prev) => prev.map((inc) => (inc.id === editRow.id ? j.data.incident : inc)));
        setEditRow(null);
        message.success("Updated");
      } else {
        message.error(j?.error?.message || "Update failed");
      }
    } catch (e) {
      message.error("Update failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, editRow, getApiToken]);

  const handleDelete = useCallback(async (rowId) => {
    if (!projectId) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/incidents/${rowId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        setIncidents((prev) => prev.filter((inc) => inc.id !== rowId));
        message.success("Deleted");
      } else {
        const j = await r.json().catch(() => ({}));
        message.error(j?.error?.message || "Delete failed");
      }
    } catch (e) {
      message.error("Delete failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, getApiToken]);

  const handleTransition = useCallback(async (action) => {
    if (!projectId) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/incidents/transition`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const j = await r.json();
      if (r.ok && j?.data) {
        setWorkflow(j.data.workflow);
        if (j.data.incidentMonitoringPackage?.version) {
          setCurrentVersion(j.data.incidentMonitoringPackage.version);
        }
        message.success(action === "restart" ? "New version started" : "Workflow advanced");
        if (action === "restart") {
          setViewingVersion(null);
          fetchIncidents(null);
        }
        if (fetchProjectProfile) fetchProjectProfile(projectId);
      } else {
        message.error(j?.error?.message || "Transition failed");
      }
    } catch (e) {
      message.error("Transition failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, getApiToken, fetchProjectProfile, fetchIncidents]);

  const handleGenerateControls = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/incidents/generate-impacted-controls`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const j = await r.json();
      if (r.ok && j?.data?.incidents) {
        setIncidents(j.data.incidents);
        const n = typeof j.data.suggestedCount === "number" ? j.data.suggestedCount : null;
        message.success(
          n === 0
            ? "The AI returned no impacted-control suggestions."
            : `AI suggestions ready${n ? ` for ${n} incident${n === 1 ? "" : "s"}` : ""}. Review each one and accept it to record it.`,
        );
      } else {
        message.error(j?.error?.message || "Generation failed");
      }
    } catch (e) {
      message.error("Generation failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, getApiToken]);

  const handleAcceptSuggestion = useCallback(async (incidentRowId) => {
    if (!projectId || !incidentRowId) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/incidents/${encodeURIComponent(incidentRowId)}/accept-impacted-controls`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j?.data?.incident) {
        setIncidents((prev) => prev.map((inc) => (inc.id === incidentRowId ? j.data.incident : inc)));
        message.success("Impacted controls accepted.");
      } else {
        message.error(j?.error?.message || "Could not accept the suggestion.");
      }
    } catch (e) {
      message.error("Could not accept the suggestion.");
    } finally {
      setBusy(false);
    }
  }, [projectId, getApiToken]);

  const handleDownloadTemplate = useCallback(() => {
    // Header-only: a sample row would import as a real incident if uploaded unedited.
    const csv = CSV_TEMPLATE_COLUMNS.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "incident_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const canAcceptSuggestion =
    (userRole === Role.ADVISOR || userRole === Role.LEAD_ADVISOR) &&
    workflow !== "published" &&
    (viewingVersion === null || viewingVersion === currentVersion);

  const columns = useMemo(() => {
    const renderAcceptedControls = (v) => {
      if (!v) return <span className="text-xs text-[color:var(--color-text-muted)]">-</span>;
      try {
        const arr = JSON.parse(v);
        if (!Array.isArray(arr) || arr.length === 0) return "-";
        const popoverContent = (
          <div className="max-h-[300px] overflow-y-auto space-y-1.5 min-w-[240px]">
            {arr.map((c) => (
              <div key={c} className="flex items-start gap-2 text-xs">
                <Tag color="blue" className="text-[10px] shrink-0 mt-0.5">{c}</Tag>
                <span className="text-[color:var(--color-text-hero)]">{controlsMap[c] || "-"}</span>
              </div>
            ))}
          </div>
        );
        return (
          <Popover content={popoverContent} title={<span className="text-xs font-bold">Impacted Controls ({arr.length})</span>} trigger="click" placement="left">
            <div className="flex flex-wrap gap-0.5 cursor-pointer hover:opacity-80">
              {arr.map((c) => (
                <Tag key={c} className="text-[9px] px-1 py-0 leading-4" color="blue">{c}</Tag>
              ))}
            </div>
          </Popover>
        );
      } catch { return "-"; }
    };
    const cols = [
      {
        title: "Incident ID",
        dataIndex: "incidentId",
        key: "incidentId",
        width: 140,
        render: (v) => <span className="font-mono text-xs text-[color:var(--color-text-muted-dark)]">{v}</span>,
      },
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        ellipsis: true,
      },
      {
        title: "Link",
        dataIndex: "link",
        key: "link",
        width: 80,
        render: (v) => v ? <a href={v} target="_blank" rel="noopener noreferrer" className="text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] text-xs">View</a> : "-",
      },
      {
        title: "Severity",
        dataIndex: "severity",
        key: "severity",
        width: 100,
        render: (v) => (SEVERITY_OPTIONS.includes(v) ? <Tag color={SEVERITY_COLORS[v]}>{v}</Tag> : <Tag data-testid="severity-unknown">Unknown</Tag>),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (v) => {
          if (!STATUS_OPTIONS.includes(v)) return <Tag data-testid="status-unknown">Unknown</Tag>;
          const color = v === "Closed" ? "green" : v === "Contained" ? "blue" : v === "In Progress" ? "orange" : "default";
          return <Tag color={color}>{v}</Tag>;
        },
      },
      {
        title: "Date Detected",
        dataIndex: "dateDetected",
        key: "dateDetected",
        width: 120,
        render: (v) => <span className="text-xs text-[color:var(--color-text-muted)]">{v || "-"}</span>,
      },
      {
        title: "Owner",
        dataIndex: "owner",
        key: "owner",
        width: 160,
        render: (v) => v || "-",
      },
      {
        title: "Impacted Controls",
        dataIndex: "impactedControls",
        key: "impactedControls",
        width: 220,
        render: (v, row) => (
          <div className="space-y-2">
            {renderAcceptedControls(v)}
            <ImpactedControlsSuggestion row={row} canAccept={canAcceptSuggestion} busy={busy} onAccept={handleAcceptSuggestion} />
          </div>
        ),
      },
    ];

    if (canEdit) {
      cols.push({
        title: "Actions",
        key: "actions",
        width: 100,
        render: (_, row) => (
          <Space size="small">
            <Tooltip title="Edit">
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditRow({ ...row })} />
            </Tooltip>
            <Tooltip title="Delete">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row.id)} disabled={busy} />
            </Tooltip>
          </Space>
        ),
      });
    }

    return cols;
  }, [canEdit, busy, handleDelete, controlsMap, canAcceptSuggestion, handleAcceptSuggestion]);

  const canGenerateControls = userRole === Role.ADVISOR && incidents.length > 0 && workflow === "advisor_review";

  return (
    <div className="space-y-6">
      <div>
        <StageHeader title="Evidencing & Monitoring" />
        <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">Manage incident logs, threat intelligence, and evidence collection.</p>
      </div>

      <SubStepNav
        ariaLabel="Evidencing and monitoring sections"
        steps={SUB_TABS.map((tab) => ({ key: tab.key, label: tab.label }))}
        activeKey={activeTab}
        onSelect={setActiveTab}
      />

      {activeTab === "incidents" && (
        <IncidentsSubTab
          incidents={incidents}
          workflow={workflow}
          wfMeta={wfMeta}
          canEdit={canEdit && (viewingVersion === null || viewingVersion === currentVersion)}
          cta={(viewingVersion === null || viewingVersion === currentVersion) ? cta : null}
          busy={busy}
          columns={columns}
          userRole={userRole}
          canGenerateControls={canGenerateControls && (viewingVersion === null || viewingVersion === currentVersion)}
          currentVersion={currentVersion}
          viewingVersion={viewingVersion}
          onVersionChange={(v) => { setViewingVersion(v); fetchIncidents(v); }}
          onUploadCsv={handleUploadCsv}
          onAddRow={() => setAddModalOpen(true)}
          onTransition={handleTransition}
          onGenerateControls={handleGenerateControls}
          onDownloadTemplate={handleDownloadTemplate}
          onViewTemplate={() => setTemplateModalOpen(true)}
        />
      )}

      {activeTab === "evidence" && (
        <EvidenceSubTab project={project} user={user} getApiToken={getApiToken} />
      )}

      {activeTab === "dashboard" && (
        <DashboardSubTab project={project} getApiToken={getApiToken} />
      )}

      {/* View Template Modal */}
      <Modal
        title="CSV Template - Expected Columns"
        open={templateModalOpen}
        onCancel={() => setTemplateModalOpen(false)}
        footer={null}
        width={520}
      >
        <div className="space-y-3">
          <p className="text-sm text-[color:var(--color-text-muted-dark)]">Your CSV file should contain the following columns (header row required):</p>
          <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[color:var(--color-border)]">
                  <th className="text-left py-2 font-bold text-[color:var(--color-text-hero)]">#</th>
                  <th className="text-left py-2 font-bold text-[color:var(--color-text-hero)]">Column Name</th>
                  <th className="text-left py-2 font-bold text-[color:var(--color-text-hero)]">Required</th>
                </tr>
              </thead>
              <tbody>
                {CSV_TEMPLATE_COLUMNS.map((col, i) => (
                  <tr key={col} className="border-b border-[color:var(--color-border)] last:border-0">
                    <td className="py-1.5 text-[color:var(--color-text-muted)]">{i + 1}</td>
                    <td className="py-1.5 font-medium text-[color:var(--color-text-hero)]">{col}</td>
                    <td className="py-1.5">{i < 2 ? <Tag color="red" className="text-[10px]">Required</Tag> : <span className="text-[color:var(--color-text-muted)]">Optional</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[color:var(--color-text-muted)]">Severity values: Low, Medium, High, Critical</p>
        </div>
      </Modal>

      {/* Add Row Modal */}
      <Modal
        title="Add Incident Row"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={handleAddRow}
        okText="Add"
        okButtonProps={{ disabled: !(newRow.incidentId.trim() && newRow.title.trim() && newRow.severity && newRow.status) }}
        confirmLoading={busy}
        width={520}
      >
        <div className="space-y-3 mt-4">
          <div>
            <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Incident ID *</label>
            <Input value={newRow.incidentId} onChange={(e) => setNewRow((p) => ({ ...p, incidentId: e.target.value }))} placeholder="INC-2026-004" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Title *</label>
            <Input value={newRow.title} onChange={(e) => setNewRow((p) => ({ ...p, title: e.target.value }))} placeholder="Incident title" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Link</label>
            <Input value={newRow.link} onChange={(e) => setNewRow((p) => ({ ...p, link: e.target.value }))} placeholder="https://..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Severity *</label>
              <Select className="w-full" placeholder="Select severity" value={newRow.severity || undefined} onChange={(v) => setNewRow((p) => ({ ...p, severity: v }))}>
                {SEVERITY_OPTIONS.map((s) => <Select.Option key={s} value={s}>{s}</Select.Option>)}
              </Select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Status *</label>
              <Select className="w-full" placeholder="Select status" value={newRow.status || undefined} onChange={(v) => setNewRow((p) => ({ ...p, status: v }))}>
                {STATUS_OPTIONS.map((s) => <Select.Option key={s} value={s}>{s}</Select.Option>)}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Date Detected</label>
              <Input value={newRow.dateDetected} onChange={(e) => setNewRow((p) => ({ ...p, dateDetected: e.target.value }))} placeholder="DD/MM/YYYY" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Owner</label>
              <Input value={newRow.owner} onChange={(e) => setNewRow((p) => ({ ...p, owner: e.target.value }))} placeholder="Name (Dept)" />
            </div>
          </div>
        </div>
      </Modal>

      {/* CSV import result */}
      <Modal
        title="CSV import result"
        open={!!csvResult}
        onCancel={() => setCsvResult(null)}
        onOk={() => setCsvResult(null)}
        okText="Close"
        cancelButtonProps={{ style: { display: "none" } }}
        width={560}
      >
        {csvResult && (
          <div className="space-y-3 mt-2" data-testid="csv-import-result">
            {csvResult.error && <p className="text-sm font-semibold text-[color:var(--color-danger-fg)]">{csvResult.error}</p>}
            <p data-testid="csv-inserted" className="text-sm font-bold text-[color:var(--color-text-hero)]">{csvResult.inserted} incident(s) imported</p>
            {csvResult.rejected.length > 0 ? (
              <div>
                <p data-testid="csv-rejected-count" className="text-sm font-bold text-[color:var(--color-warn-fg)]">{csvResult.rejected.length} row(s) rejected</p>
                <ul className="mt-1 max-h-60 overflow-y-auto text-xs text-[color:var(--color-text-hero)] space-y-1">
                  {csvResult.rejected.map((rj, i) => (
                    <li key={i} data-testid="csv-rejected-row">Line {rj.line}: {rj.reason}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-xs text-[color:var(--color-text-muted)]">No rows were rejected.</p>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Row Modal */}
      <Modal
        title="Edit Incident"
        open={!!editRow}
        onCancel={() => setEditRow(null)}
        onOk={handleEditRow}
        okText="Save"
        confirmLoading={busy}
        width={520}
      >
        {editRow && (
          <div className="space-y-3 mt-4">
            <div>
              <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Incident ID</label>
              <Input value={editRow.incidentId} onChange={(e) => setEditRow((p) => ({ ...p, incidentId: e.target.value }))} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Title</label>
              <Input value={editRow.title} onChange={(e) => setEditRow((p) => ({ ...p, title: e.target.value }))} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Link</label>
              <Input value={editRow.link || ""} onChange={(e) => setEditRow((p) => ({ ...p, link: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Severity</label>
                <Select className="w-full" value={editRow.severity} onChange={(v) => setEditRow((p) => ({ ...p, severity: v }))}>
                  {SEVERITY_OPTIONS.map((s) => <Select.Option key={s} value={s}>{s}</Select.Option>)}
                </Select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Status</label>
                <Select className="w-full" value={editRow.status} onChange={(v) => setEditRow((p) => ({ ...p, status: v }))}>
                  {STATUS_OPTIONS.map((s) => <Select.Option key={s} value={s}>{s}</Select.Option>)}
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Date Detected</label>
                <Input value={editRow.dateDetected || ""} onChange={(e) => setEditRow((p) => ({ ...p, dateDetected: e.target.value }))} />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Owner</label>
                <Input value={editRow.owner || ""} onChange={(e) => setEditRow((p) => ({ ...p, owner: e.target.value }))} />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function IncidentsSubTab({
  incidents,
  workflow,
  wfMeta,
  canEdit,
  cta,
  busy,
  columns,
  userRole,
  canGenerateControls,
  currentVersion,
  viewingVersion,
  onVersionChange,
  onUploadCsv,
  onAddRow,
  onTransition,
  onGenerateControls,
  onDownloadTemplate,
  onViewTemplate,
}) {
  return (
    <div className="space-y-4">
      {/* Workflow Status Banner */}
      <div className="flex items-center justify-between rounded-2xl border border-[color:var(--color-border)] bg-gradient-to-r from-[color:var(--color-surface-sunken)] to-[color:var(--color-bg-white)] px-5 py-3">
        <div className="flex items-center gap-3">
          <Tag color={wfMeta.color} className="text-xs font-bold">{wfMeta.label}</Tag>
          <span className="text-xs text-[color:var(--color-text-muted)]">Owner: <span className="font-semibold text-[color:var(--color-text-hero)]">{wfMeta.owner}</span></span>
          {currentVersion > 1 && (
            <Select
              size="small"
              value={viewingVersion || currentVersion}
              onChange={(v) => onVersionChange(v)}
              className="w-[120px]"
              options={Array.from({ length: currentVersion }, (_, i) => ({
                value: i + 1,
                label: `Version ${i + 1}${i + 1 === currentVersion ? " (latest)" : ""}`,
              }))}
            />
          )}
          {currentVersion === 1 && (
            <span className="text-[10px] font-semibold text-[color:var(--color-text-muted)] bg-[color:var(--color-bg-alt)] rounded-full px-2 py-0.5">Version 1</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canGenerateControls && (
            <Button
              icon={<ThunderboltOutlined />}
              loading={busy}
              onClick={onGenerateControls}
              className="text-xs"
            >
              Generate Impacted Controls
            </Button>
          )}
          {cta && (() => {
            const needsImpactedControls = cta.action === "submit_for_spoc_approval" && incidents.length > 0 && !incidents.some((inc) => inc.impactedControls);
            return (
              <Tooltip title={needsImpactedControls ? "Generate Impacted Controls before submitting" : ""}>
                <Button type="primary" loading={busy} disabled={needsImpactedControls} onClick={() => onTransition(cta.action)}>
                  {cta.label}
                </Button>
              </Tooltip>
            );
          })()}
        </div>
      </div>

      {viewingVersion && viewingVersion !== currentVersion && (
        <div className="rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-2 flex items-center justify-between">
          <span className="text-xs font-semibold text-[color:var(--color-warn-fg)]">Viewing Version {viewingVersion} (read-only). Latest is Version {currentVersion}.</span>
          <Button size="small" type="link" onClick={() => onVersionChange(currentVersion)} className="text-xs">Switch to latest</Button>
        </div>
      )}

      {/* Incident Management Log Header */}
      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm">
        <div className="flex items-center justify-between border-b border-[color:var(--color-border)] px-6 py-4">
          <h3 className="text-lg font-bold text-[color:var(--color-text-hero)]">Incident Management Log</h3>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Upload
                accept=".csv"
                showUploadList={false}
                beforeUpload={onUploadCsv}
                disabled={busy}
              >
                <Button type="primary" danger icon={<UploadOutlined />} loading={busy} className="text-xs font-semibold">
                  UPLOAD CSV
                </Button>
              </Upload>
            )}
            <Button icon={<EyeOutlined />} onClick={onViewTemplate} className="text-xs">
              VIEW TEMPLATE
            </Button>
            <Button icon={<DownloadOutlined />} onClick={onDownloadTemplate} className="text-xs">
              DOWNLOAD TEMPLATE
            </Button>
            {canEdit && (
              <Button icon={<PlusOutlined />} onClick={onAddRow} disabled={busy} className="text-xs">
                ADD ROW
              </Button>
            )}
          </div>
        </div>

        {incidents.length === 0 ? (
          <div className="py-16">
            <Empty description="No incidents uploaded yet. Upload a CSV or add a row manually." />
          </div>
        ) : (
          <Table
            dataSource={incidents}
            columns={columns}
            rowKey="id"
            size="small"
            pagination={incidents.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
            className="policylens-data-table"
            scroll={{ x: 1000 }}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Evidence Sub-Tab
// ============================================================================

const EVIDENCE_EXTERNAL_STATUS_OPTIONS = [
  "Drafting",
  "Delegate Review",
  "SPOC Review",
  "Published",
  "Training",
  "Implemented",
  "Other",
];

const EMPTY_EXTERNAL_FORM = {
  title: "",
  controlRef: "",
  status: "Published",
  owner: "",
  version: 1,
  versionDate: "",
  fileUrl: "",
};

const EVIDENCE_WF_LABELS = {
  not_started: { label: "Not Started", color: "default", owner: "Lead Advisor" },
  pushed_to_spoc: { label: "SPOC Review", color: "blue", owner: "Company SPOC" },
  delegate_review: { label: "Delegate Review", color: "purple", owner: "Company Delegate" },
  advisor_review: { label: "Advisor Review", color: "geekblue", owner: "Advisor" },
  spoc_finalize: { label: "Finalization", color: "orange", owner: "Company SPOC" },
  finalized: { label: "Finalized", color: "green", owner: "-" },
};

function canEditEvidence(role, workflow) {
  if (role === Role.ADMIN) return true;
  switch (workflow) {
    case "pushed_to_spoc": return role === Role.COMPANY_SPOC;
    case "delegate_review": return role === Role.COMPANY_DELEGATE;
    case "advisor_review": return role === Role.ADVISOR || role === Role.LEAD_ADVISOR;
    case "spoc_finalize": return role === Role.COMPANY_SPOC;
    default: return false;
  }
}

function canRunEvidenceAiScore(role, workflow) {
  if (workflow !== "advisor_review") return false;
  return role === Role.ADVISOR || role === Role.LEAD_ADVISOR || role === Role.ADMIN;
}

/** AI score cell: a real number only for a real score; a failed attempt says so and is never shown as 0. */
export function AiScoreCell({ row }) {
  if ((row.category || "platform") === "external") {
    return <span className="text-xs text-[color:var(--color-text-muted)]">-</span>;
  }
  if (row.hasAiScore && row.aiOverallScore != null) {
    return <span className={`text-sm ${aiScoreColorClass(row.aiOverallScore)}`}>{row.aiOverallScore}%</span>;
  }
  if (row.aiScoringFailed) {
    return (
      <Tag color="red" className="text-[10px]" title={row.aiScoringError || undefined} data-testid="ai-scoring-failed">
        Scoring failed - retry
      </Tag>
    );
  }
  return <Tag className="text-[10px]">Not scored</Tag>;
}

function aiScoreColorClass(score) {
  if (score == null || Number.isNaN(score)) return "text-[color:var(--color-text-muted)]";
  if (score >= 75) return "text-[color:var(--color-ok-fg)] font-bold";
  if (score >= 50) return "text-[color:var(--color-warn-fg)] font-bold";
  return "text-[color:var(--color-danger-fg)] font-bold";
}

function AiDimensionBar({ label, value }) {
  const v = typeof value === "number" ? value : 0;
  const barColor = v >= 75 ? "bg-[color:var(--color-ok-fg)]" : v >= 50 ? "bg-[color:var(--color-warn-fg)]" : "bg-[color:var(--color-danger-fg)]";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[11px] text-[color:var(--color-text-muted-dark)]">
        <span>{label}</span>
        <span className="font-semibold">{v}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[color:var(--color-bg-alt)] overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(100, Math.max(0, v))}%` }} />
      </div>
    </div>
  );
}

function EvidenceSubTab({ project, user, getApiToken }) {
  const { fetchProjectProfile } = useApp();
  const [rows, setRows] = useState([]);
  const [evWorkflow, setEvWorkflow] = useState("not_started");
  const [availableDocs, setAvailableDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addDocMode, setAddDocMode] = useState("platform");
  const [selectedDocToAdd, setSelectedDocToAdd] = useState(null);
  const [externalForm, setExternalForm] = useState({ ...EMPTY_EXTERNAL_FORM });
  const [aiScoring, setAiScoring] = useState(false);
  const [aggregateAiScore, setAggregateAiScore] = useState(null);
  const loadedRef = useRef(false);

  const projectId = project?.id;
  const companyDisplayName = project?.companyName || project?.name || "";
  const userRole = user?.role || "";

  const syncEvidence = useCallback(async () => {
    if (!projectId) return;
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/evidence-register/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const j = await r.json();
      if (j?.data) {
        setRows(j.data.rows || []);
        setEvWorkflow(j.data.evidenceWorkflow || "not_started");
        setAvailableDocs(j.data.availableDocs || []);
      }
    } catch (e) {
      console.error("evidence sync:", e);
    }
  }, [projectId, getApiToken]);

  useEffect(() => {
    if (!projectId || loadedRef.current) return;
    loadedRef.current = true;
    syncEvidence();
  }, [projectId, syncEvidence]);

  useEffect(() => {
    if (!drawerRow) return;
    const key = drawerRow.rowKey || drawerRow.controlId;
    const updated = rows.find((row) => (row.rowKey || row.controlId) === key);
    if (updated) setDrawerRow(updated);
  }, [rows, drawerRow?.rowKey, drawerRow?.controlId]);

  const resetAddModal = useCallback(() => {
    setSelectedDocToAdd(null);
    setAddDocMode("platform");
    setExternalForm({ ...EMPTY_EXTERNAL_FORM });
  }, []);

  const handleTransition = useCallback(async (action) => {
    if (!projectId) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/evidence-register/transition`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const j = await r.json();
      if (r.ok && j?.data) {
        setEvWorkflow(j.data.evidenceWorkflow);
        message.success("Workflow advanced");
        if (fetchProjectProfile) fetchProjectProfile(projectId);
      } else {
        message.error(j?.error?.message || "Transition failed");
      }
    } catch (e) {
      message.error("Transition failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, getApiToken, fetchProjectProfile]);

  const handleSaveDraft = useCallback(async (controlId, html) => {
    if (!projectId) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/evidence-register/${controlId}/draft`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ html }),
      });
      const j = await r.json();
      if (r.ok && j?.data) {
        setRows((prev) => prev.map((row) =>
          row.controlId === controlId
            ? { ...row, version: j.data.version, versionDate: j.data.versionDate, draftHtml: html }
            : row
        ));
        setDrawerRow((prev) => prev && prev.controlId === controlId ? { ...prev, version: j.data.version, versionDate: j.data.versionDate, draftHtml: html } : prev);
        message.success("Document saved - version updated");
      } else {
        message.error(j?.error?.message || "Save failed");
      }
    } catch (e) {
      message.error("Save failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, getApiToken]);

  const handleAddPlatformRow = useCallback(async () => {
    if (!projectId || !selectedDocToAdd) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/evidence-register/add-row`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ controlId: selectedDocToAdd }),
      });
      const j = await r.json();
      if (r.ok && j?.data) {
        message.success("Document added to evidence register");
        setAddModalOpen(false);
        resetAddModal();
        syncEvidence();
        if (fetchProjectProfile) fetchProjectProfile(projectId);
      } else {
        message.error(j?.error?.message || "Add failed");
      }
    } catch (e) {
      message.error("Add failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, selectedDocToAdd, getApiToken, syncEvidence, fetchProjectProfile, resetAddModal]);

  const handleAddExternalRow = useCallback(async () => {
    if (!projectId) return;
    const title = externalForm.title?.trim();
    const controlRef = externalForm.controlRef?.trim();
    const owner = externalForm.owner?.trim();
    const fileUrl = externalForm.fileUrl?.trim();
    if (!title || !controlRef || !owner || !fileUrl) {
      message.warning("Fill in title, control ref, owner, and file URL.");
      return;
    }
    setBusy(true);
    try {
      const token = await getApiToken();
      const versionDate = externalForm.versionDate?.trim()
        || new Date().toISOString().slice(0, 10);
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/evidence-register/add-external-row`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          controlRef,
          owner,
          fileUrl,
          status: externalForm.status || "Published",
          version: externalForm.version ?? 1,
          versionDate,
        }),
      });
      const j = await r.json();
      if (r.ok && j?.data) {
        message.success("External evidence added");
        setAddModalOpen(false);
        resetAddModal();
        syncEvidence();
        if (fetchProjectProfile) fetchProjectProfile(projectId);
      } else {
        message.error(j?.error?.message || "Add failed");
      }
    } catch (e) {
      message.error("Add failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, externalForm, getApiToken, syncEvidence, fetchProjectProfile, resetAddModal]);

  const handleAddDocSubmit = useCallback(() => {
    if (addDocMode === "external") {
      handleAddExternalRow();
    } else {
      handleAddPlatformRow();
    }
  }, [addDocMode, handleAddExternalRow, handleAddPlatformRow]);

  const externalFormValid = useMemo(() => {
    return !!(
      externalForm.title?.trim()
      && externalForm.controlRef?.trim()
      && externalForm.owner?.trim()
      && externalForm.fileUrl?.trim()
      && /^https?:\/\//i.test(externalForm.fileUrl.trim())
    );
  }, [externalForm]);

  const addModalOkDisabled = addDocMode === "platform" ? !selectedDocToAdd : !externalFormValid;

  const runAiScore = useCallback(async () => {
    if (!projectId) return;
    setAiScoring(true);
    try {
      const token = await getApiToken();
      const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/evidence-register/ai-score`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const j = await r.json();
      if (r.ok && j?.data) {
        setRows(j.data.rows || []);
        setAggregateAiScore(j.data.aggregateScore != null ? j.data.aggregateScore : null);
        // Only report figures the server returned; a missing figure is omitted, never shown as 0.
        const scoredOfTotal = typeof j.data.scored === "number" && typeof j.data.total === "number" ? ` - ${j.data.scored}/${j.data.total} documents` : "";
        const avgPart = j.data.aggregateScore != null ? ` (avg ${j.data.aggregateScore}%)` : "";
        if (typeof j.data.failed === "number" && j.data.failed > 0) {
          message.warning(`AI scoring finished${scoredOfTotal}${avgPart}; ${j.data.failed} document${j.data.failed === 1 ? "" : "s"} could not be scored (marked "Scoring failed - retry"). Failed documents are not counted in the average.`, 8);
        } else {
          message.success(`AI scoring complete${scoredOfTotal}${avgPart}`);
        }
        if (fetchProjectProfile) fetchProjectProfile(projectId);
      } else {
        message.error(j?.error?.message || "AI scoring failed");
      }
    } catch (e) {
      message.error("AI scoring failed");
    } finally {
      setAiScoring(false);
    }
  }, [projectId, getApiToken, fetchProjectProfile]);

  const handleAiScoreClick = useCallback(() => {
    const hasExisting = rows.some((row) => row.hasAiScore);
    if (hasExisting) {
      Modal.confirm({
        title: "Re-score all documents?",
        content: "This will replace existing AI scores for every document in the evidence register.",
        okText: "Score with AI",
        onOk: () => runAiScore(),
      });
    } else {
      runAiScore();
    }
  }, [rows, runAiScore]);

  const wfMeta = EVIDENCE_WF_LABELS[evWorkflow] || EVIDENCE_WF_LABELS.not_started;
  const cta = getEvidenceCta(userRole, evWorkflow);
  const editable = canEditEvidence(userRole, evWorkflow);
  const canAddRow = editable;
  const canAiScore = canRunEvidenceAiScore(userRole, evWorkflow);
  const platformRows = useMemo(() => rows.filter((row) => (row.category || "platform") !== "external"), [rows]);
  const allDocsScored = platformRows.length > 0 && platformRows.every((row) => row.hasAiScore);
  const hasAnyAiScores = rows.some((row) => row.hasAiScore);
  const showAiScoreColumn = canAiScore || hasAnyAiScores;
  const computedAggregate = useMemo(() => {
    const scored = rows.filter((row) => row.hasAiScore && row.aiOverallScore != null);
    if (!scored.length) return null;
    return Math.round(scored.reduce((sum, row) => sum + row.aiOverallScore, 0) / scored.length);
  }, [rows]);
  const displayAggregate = aggregateAiScore ?? computedAggregate;

  const columns = useMemo(() => [
    {
      title: "Evidence ID",
      dataIndex: "evidenceId",
      key: "evidenceId",
      width: 100,
      render: (v) => <span className="font-mono text-xs font-bold text-[color:var(--color-text-muted-dark)]">{v}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (v, row) => {
        const display = stripCompanyPrefixFromDocumentTitle(v, companyDisplayName) || "Untitled";
        return (
          <button type="button" onClick={() => setDrawerRow(row)} className="text-left text-sm font-semibold text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] hover:underline">
            {display}
          </button>
        );
      },
    },
    {
      title: "Control Ref",
      dataIndex: "controlRef",
      key: "controlRef",
      width: 100,
      render: (v) => <span className="font-mono text-xs font-bold text-[color:var(--color-accent)]">{v}</span>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 110,
      render: (v) => {
        const cat = v || "platform";
        return (
          <Tag color={cat === "external" ? "purple" : "blue"} className="text-[10px] font-semibold">
            {cat === "external" ? "External" : "Platform"}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (v) => {
        const colorMap = { Drafting: "blue", "Delegate Review": "purple", "SPOC Review": "orange", Published: "cyan", Training: "geekblue", Implemented: "green" };
        return <Tag color={colorMap[v] || "default"}>{v}</Tag>;
      },
    },
    {
      title: "Owner",
      dataIndex: "owner",
      key: "owner",
      width: 140,
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      width: 80,
      render: (v) => <span className="text-xs font-bold text-[color:var(--color-text-hero)]">v{v}</span>,
    },
    {
      title: "Version Date",
      dataIndex: "versionDate",
      key: "versionDate",
      width: 120,
      render: (v) => <span className="text-xs text-[color:var(--color-text-muted)]">{v ? formatDdMmYyyy(v) : "-"}</span>,
    },
    ...(showAiScoreColumn
      ? [{
          title: "AI Score",
          key: "aiScore",
          width: 100,
          render: (_, row) => <AiScoreCell row={row} />,
        }]
      : []),
  ], [companyDisplayName, showAiScoreColumn]);

  return (
    <div className="space-y-4">
      {/* Workflow Status Banner */}
      <div className="flex items-center justify-between rounded-2xl border border-[color:var(--color-border)] bg-gradient-to-r from-[color:var(--color-surface-sunken)] to-[color:var(--color-bg-white)] px-5 py-3">
        <div className="flex items-center gap-3">
          <Tag color={wfMeta.color} className="text-xs font-bold">{wfMeta.label}</Tag>
          <span className="text-xs text-[color:var(--color-text-muted)]">Owner: <span className="font-semibold text-[color:var(--color-text-hero)]">{wfMeta.owner}</span></span>
        </div>
        <div className="flex items-center gap-2">
          {canAiScore && (
            <Button
              icon={<ThunderboltOutlined />}
              loading={aiScoring}
              onClick={handleAiScoreClick}
            >
              {aiScoring ? "Scoring…" : "Score with AI"}
            </Button>
          )}
          {displayAggregate != null && hasAnyAiScores && (
            <Tag color="blue" className="text-xs font-semibold">
              Avg {displayAggregate}%
            </Tag>
          )}
          {cta && cta.action === "submit_for_finalization" && !allDocsScored ? (
            <Tooltip title="Score all documents with AI first.">
              <Button type="primary" disabled>
                {cta.label}
              </Button>
            </Tooltip>
          ) : cta ? (
            <Button type="primary" loading={busy} onClick={() => handleTransition(cta.action)}>
              {cta.label}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Evidence Register Table */}
      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm">
        <div className="flex items-center justify-between border-b border-[color:var(--color-border)] px-6 py-4">
          <h3 className="text-lg font-bold text-[color:var(--color-text-hero)]">Evidence Register</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[color:var(--color-text-muted)]">{rows.length} document(s)</span>
            {canAddRow && (
              <Button icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)} className="text-xs">
                Add Document
              </Button>
            )}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="py-16">
            <Empty description="No documents found. Complete Control Implementation (step 7) to populate the evidence register." />
          </div>
        ) : (
          <Table
            dataSource={rows}
            columns={columns}
            rowKey={(row) => row.rowKey || row.controlId}
            size="small"
            pagination={rows.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
            className="policylens-data-table"
            scroll={{ x: 900 }}
            onRow={(row) => ({ onClick: () => setDrawerRow(row), className: "cursor-pointer hover:bg-[color:var(--color-bg-alt)]" })}
          />
        )}
      </div>

      {/* Add Document Modal */}
      <Modal
        title="Add Document to Evidence Register"
        open={addModalOpen}
        onCancel={() => { setAddModalOpen(false); resetAddModal(); }}
        onOk={handleAddDocSubmit}
        okText="Add"
        confirmLoading={busy}
        okButtonProps={{ disabled: addModalOkDisabled }}
        width={560}
      >
        <div className="space-y-4 mt-4">
          <Radio.Group
            value={addDocMode}
            onChange={(e) => setAddDocMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            className="w-full flex"
          >
            <Radio.Button value="platform" className="flex-1 text-center">Platform document</Radio.Button>
            <Radio.Button value="external" className="flex-1 text-center">External link</Radio.Button>
          </Radio.Group>

          {addDocMode === "platform" ? (
            <>
          <p className="text-sm text-[color:var(--color-text-muted-dark)]">Select a branded document from Control Implementation. Only documents not already in the register are shown.</p>
          {availableDocs.length === 0 && (
            <p className="text-xs text-[color:var(--color-warn-fg)]">No additional platform documents available. Use External link to add other evidence.</p>
          )}
          <Select
            className="w-full"
            placeholder="Search and select a document..."
            showSearch
            optionFilterProp="label"
            value={selectedDocToAdd}
            onChange={(v) => setSelectedDocToAdd(v)}
            options={availableDocs.map((d) => ({
              value: d.controlId,
              label: `${d.controlRef} - ${stripCompanyPrefixFromDocumentTitle(d.documentName, companyDisplayName)}`,
            }))}
          />
          {selectedDocToAdd && (() => {
            const doc = availableDocs.find((d) => d.controlId === selectedDocToAdd);
            return doc ? (
              <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-3">
                <div className="text-sm font-semibold text-[color:var(--color-text-hero)]">
                  {stripCompanyPrefixFromDocumentTitle(doc.documentName, companyDisplayName)}
                </div>
                <div className="text-xs text-[color:var(--color-text-muted)] mt-1">Control: <span className="font-mono font-bold text-[color:var(--color-accent)]">{doc.controlRef}</span></div>
              </div>
            ) : null;
          })()}
            </>
          ) : (
            <>
              <p className="text-sm text-[color:var(--color-text-muted-dark)]">Add evidence hosted outside PolicyLens. Provide metadata and a link to the file.</p>
              <Input
                placeholder="Title"
                value={externalForm.title}
                onChange={(e) => setExternalForm((f) => ({ ...f, title: e.target.value }))}
              />
              <Input
                placeholder="Control reference (e.g. A.5.4)"
                value={externalForm.controlRef}
                onChange={(e) => setExternalForm((f) => ({ ...f, controlRef: e.target.value }))}
              />
              <Select
                className="w-full"
                placeholder="Status"
                value={externalForm.status}
                onChange={(v) => setExternalForm((f) => ({ ...f, status: v }))}
                options={EVIDENCE_EXTERNAL_STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
              />
              <Input
                placeholder="Owner"
                value={externalForm.owner}
                onChange={(e) => setExternalForm((f) => ({ ...f, owner: e.target.value }))}
              />
              <div className="flex gap-3">
                <InputNumber
                  className="flex-1"
                  min={1}
                  placeholder="Version"
                  value={externalForm.version}
                  onChange={(v) => setExternalForm((f) => ({ ...f, version: v ?? 1 }))}
                />
                <Input
                  className="flex-1"
                  type="date"
                  value={externalForm.versionDate}
                  onChange={(e) => setExternalForm((f) => ({ ...f, versionDate: e.target.value }))}
                />
              </div>
              <Input
                placeholder="File URL (https://...)"
                value={externalForm.fileUrl}
                onChange={(e) => setExternalForm((f) => ({ ...f, fileUrl: e.target.value }))}
              />
            </>
          )}
        </div>
      </Modal>

      {/* Document Drawer */}
      {drawerRow && (
        <EvidenceDocDrawer
          row={drawerRow}
          open={!!drawerRow}
          onClose={() => setDrawerRow(null)}
          projectId={projectId}
          companyDisplayName={companyDisplayName}
          getApiToken={getApiToken}
          readOnly={!editable}
          onSaveDraft={handleSaveDraft}
        />
      )}
    </div>
  );
}

// ============================================================================
// Dashboard Sub-Tab
// ============================================================================

function RadialGauge({ score, maxScore = 5, size = 160, strokeWidth = 14 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(score / maxScore, 1);
  const dashOffset = circumference * (1 - progress);
  const colorClass = progress >= 0.7 ? "stroke-[color:var(--color-ok-fg)]" : progress >= 0.4 ? "stroke-[color:var(--color-warn-fg)]" : "stroke-[color:var(--color-danger-fg)]";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-[color:var(--color-text-muted)]" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset} className={colorClass} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black text-[color:var(--color-text-hero)]">{score.toFixed(1)}</span>
        <span className="text-[10px] text-[color:var(--color-text-muted)] font-semibold">out of {maxScore}.0</span>
      </div>
    </div>
  );
}

function DomainBar({ domain, score, maxScore = 5 }) {
  const pct = maxScore > 0 ? Math.min((score / maxScore) * 100, 100) : 0;
  const barColor = pct >= 70 ? "bg-[color:var(--color-ok-fg)]" : pct >= 40 ? "bg-[color:var(--color-warn-fg)]" : "bg-[color:var(--color-danger-fg)]";
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-[color:var(--color-text-hero)] w-[180px] truncate font-medium">{domain}</span>
      <div className="flex-1 h-2.5 rounded-full bg-[color:var(--color-bg-alt)] overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%`, transition: "width 0.6s ease" }} />
      </div>
      <span className="text-xs font-bold text-[color:var(--color-text-hero)] w-8 text-right">{score.toFixed(1)}</span>
    </div>
  );
}

function MetricCard({ label, value, unit, color, badge, note, unavailableReason }) {
  const colorCls = color === "green" ? "text-[color:var(--color-ok-fg)]" : color === "amber" ? "text-[color:var(--color-warn-fg)]" : color === "red" ? "text-[color:var(--color-danger-fg)]" : "text-[color:var(--color-text-hero)]";
  return (
    <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-4 space-y-1">
      <div className="text-xs text-[color:var(--color-text-muted)] font-medium">{label}</div>
      {unavailableReason !== undefined ? (
        <div data-testid="metric-unavailable">
          <div className="text-sm font-bold text-[color:var(--color-text-hero)]">Not available</div>
          {unavailableReason && <div className="text-[11px] text-[color:var(--color-text-muted)]">{unavailableReason}</div>}
        </div>
      ) : (
        <div className={`text-2xl font-black ${colorCls}`}>
          {value != null ? value : "-"}
          {unit && value != null && <span className="text-sm font-semibold text-[color:var(--color-text-muted)] ml-1">{unit}</span>}
        </div>
      )}
      {badge}
      {note && <div className="text-[11px] font-semibold text-[color:var(--color-warn-fg)]">{note}</div>}
    </div>
  );
}

function DashboardSubTab({ project, getApiToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);
  const projectId = project?.id;

  useEffect(() => {
    if (!projectId || loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const token = await getApiToken();
        const r = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/evidence-register/dashboard`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const j = await r.json();
        if (j?.data) setData(j.data);
      } catch (e) {
        console.error("dashboard fetch:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, getApiToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse space-y-6 w-full max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-52 rounded-2xl bg-[color:var(--color-bg-alt)]" />
            <div className="h-52 rounded-2xl bg-[color:var(--color-bg-alt)]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-44 rounded-2xl bg-[color:var(--color-bg-alt)]" />
            <div className="h-44 rounded-2xl bg-[color:var(--color-bg-alt)]" />
          </div>
          <div className="h-28 rounded-2xl bg-[color:var(--color-bg-alt)]" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-[color:var(--color-text-muted)]">
        <EyeOutlined className="text-4xl mb-3" />
        <p className="text-sm font-semibold">Dashboard data unavailable.</p>
      </div>
    );
  }

  const { overallMaturity, domainMaturity, controlImplementation: ci, complianceReadiness, keyMetrics } = data;
  // Server-decided availability of the maturity figures: 'pushed' | 'unvalidated_estimate' (staff only,
  // not for the client) | 'unavailable'. Null scores are never drawn as zero bars or gauges.
  const maturity = data.maturity || {};
  const maturityReason = typeof maturity.reason === "string" ? maturity.reason : "";
  const overallScoreKnown = typeof overallMaturity?.score === "number";
  const domainRows = Array.isArray(domainMaturity) ? domainMaturity : [];
  const domainRowsWithScore = domainRows.filter((d) => typeof d.score === "number");
  // Audit readiness availability, decided by the server: 'validated' | 'unvalidated_estimate' (staff only) | 'unavailable'.
  const auditReadiness = data.auditReadiness || {};
  const auditScoreKnown = typeof keyMetrics?.auditReadinessScore === "number";
  const auditUnavailable = auditReadiness.availability === "unavailable" || !auditScoreKnown;
  const phaseProgressKnown = typeof overallMaturity?.phaseProgress === "number";

  const statusPill = (status) => {
    const map = { on_track: { label: "On Track", color: "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)]" }, in_progress: { label: "In Progress", color: "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)]" }, at_risk: { label: "At Risk", color: "bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)]" } };
    const s = map[status] || map.at_risk;
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.color}`}>{s.label}</span>;
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Row 1: Overall Maturity + Domain Maturity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Overall Maturity */}
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-gradient-to-br from-[color:var(--color-surface-sunken)] to-[color:var(--color-bg-white)] p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-bold text-[color:var(--color-text-hero)] uppercase tracking-wide">Overall Maturity</h4>
            {overallScoreKnown && overallMaturity.lastUpdated && (
              <span className="text-[10px] text-[color:var(--color-text-muted)] font-medium">
                Last updated: {formatDdMmYyyy(overallMaturity.lastUpdated)}
              </span>
            )}
          </div>
          {overallScoreKnown ? (
            <div className="flex items-center gap-6">
              <RadialGauge score={overallMaturity.score} />
              <div className="space-y-2">
                <div className="text-xs text-[color:var(--color-text-muted)]">
                  {maturity.availability === "pushed" ? (
                    <Tag color="green" className="text-[10px]">Validated</Tag>
                  ) : maturity.availability === "unvalidated_estimate" ? (
                    <Tag className="text-[10px]">Estimated</Tag>
                  ) : null}
                </div>
                {maturity.availability === "unvalidated_estimate" && maturityReason && (
                  <p data-testid="maturity-estimate-reason" className="max-w-[16rem] text-[11px] font-semibold text-[color:var(--color-warn-fg)]">{maturityReason}</p>
                )}
                {phaseProgressKnown && (
                  <>
                    <div className="text-xs text-[color:var(--color-text-muted-dark)]">
                      <span className="font-bold">{overallMaturity.phaseProgress}%</span> engagement phase progress
                    </div>
                    <div className="w-32 h-1.5 rounded-full bg-[color:var(--color-bg-alt)] overflow-hidden">
                      <div className="h-full rounded-full bg-[color:var(--color-accent)]" style={{ width: `${overallMaturity.phaseProgress}%` }} />
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div data-testid="maturity-unavailable" className="space-y-1">
              <p className="text-sm font-bold text-[color:var(--color-text-hero)]">Not available</p>
              {maturityReason && <p className="text-xs text-[color:var(--color-text-muted)]">{maturityReason}</p>}
            </div>
          )}
        </div>

        {/* Domain-Level Maturity */}
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-gradient-to-br from-[color:var(--color-surface-sunken)] to-[color:var(--color-bg-white)] p-6 shadow-sm">
          <h4 className="text-sm font-bold text-[color:var(--color-text-hero)] uppercase tracking-wide mb-4">Domain-Level Maturity</h4>
          {domainRowsWithScore.length === 0 ? (
            <div data-testid="domain-maturity-unavailable" className="space-y-1">
              <p className="text-sm font-bold text-[color:var(--color-text-hero)]">Not available</p>
              {maturityReason && <p className="text-xs text-[color:var(--color-text-muted)]">{maturityReason}</p>}
            </div>
          ) : (
            <div className="space-y-3">
              {domainRowsWithScore.map((d) => (
                <DomainBar key={d.domain} domain={d.domain} score={d.score} maxScore={d.maxScore} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Control Implementation + Compliance Readiness */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Control Implementation */}
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-gradient-to-br from-[color:var(--color-surface-sunken)] to-[color:var(--color-bg-white)] p-6 shadow-sm">
          <h4 className="text-sm font-bold text-[color:var(--color-text-hero)] uppercase tracking-wide mb-4">Control Implementation</h4>
          {ci.total === 0 ? (
            <p className="text-xs text-[color:var(--color-text-muted)]">No controls in the implementation register yet.</p>
          ) : (
            <div className="space-y-3">
              {[
                { label: "Fully Implemented", count: ci.implemented, color: "bg-[color:var(--color-ok-fg)]" },
                { label: "Partially Implemented", count: ci.partiallyImplemented, color: "bg-[color:var(--color-warn-fg)]" },
                { label: "Not Implemented", count: ci.notImplemented, color: "bg-[color:var(--color-danger-fg)]" },
                { label: "Not Applicable", count: ci.notApplicable, color: "bg-[color:var(--color-bg-alt)]" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                  <span className="text-xs text-[color:var(--color-text-hero)] flex-1 font-medium">{item.label}</span>
                  <span className="text-xs font-bold text-[color:var(--color-text-hero)]">{item.count}</span>
                  <span className="text-[10px] text-[color:var(--color-text-muted)] w-12 text-right">/{ci.total + ci.notApplicable}</span>
                </div>
              ))}
              <div className="mt-2 h-3 rounded-full bg-[color:var(--color-bg-alt)] overflow-hidden flex">
                {ci.total > 0 && (
                  <>
                    <div className="h-full bg-[color:var(--color-ok-fg)]" style={{ width: `${(ci.implemented / (ci.total + ci.notApplicable)) * 100}%` }} />
                    <div className="h-full bg-[color:var(--color-warn-fg)]" style={{ width: `${(ci.partiallyImplemented / (ci.total + ci.notApplicable)) * 100}%` }} />
                    <div className="h-full bg-[color:var(--color-danger-fg)]" style={{ width: `${(ci.notImplemented / (ci.total + ci.notApplicable)) * 100}%` }} />
                    <div className="h-full bg-[color:var(--color-bg-alt)]" style={{ width: `${(ci.notApplicable / (ci.total + ci.notApplicable)) * 100}%` }} />
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Compliance Readiness */}
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-gradient-to-br from-[color:var(--color-surface-sunken)] to-[color:var(--color-bg-white)] p-6 shadow-sm">
          <h4 className="text-sm font-bold text-[color:var(--color-text-hero)] uppercase tracking-wide mb-4">Compliance Readiness</h4>
          {complianceReadiness.length === 0 ? (
            <p data-testid="compliance-readiness-empty" className="text-xs text-[color:var(--color-text-muted)]">No compliance readiness is available to show yet. Figures appear here once they have been validated.</p>
          ) : (
            <div className="space-y-3">
              {complianceReadiness.map((fw) => (
                <div key={fw.framework} className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-[color:var(--color-text-hero)]">
                      {fw.framework}
                      {fw.validation === "unvalidated_estimate" && <Tag className="ml-2 text-[10px]">Unvalidated estimate</Tag>}
                    </div>
                    {typeof fw.percent === "number" ? (
                      <>
                        <div className="mt-1 w-32 h-1.5 rounded-full bg-[color:var(--color-bg-alt)] overflow-hidden">
                          <div
                            className={`h-full rounded-full ${fw.status === "on_track" ? "bg-[color:var(--color-ok-fg)]" : fw.status === "in_progress" ? "bg-[color:var(--color-warn-fg)]" : "bg-[color:var(--color-danger-fg)]"}`}
                            style={{ width: `${fw.percent}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-[color:var(--color-text-muted)] font-medium">{fw.percent}% ready</span>
                      </>
                    ) : (
                      <span className="text-[10px] text-[color:var(--color-text-muted)] font-medium">Not available</span>
                    )}
                  </div>
                  {typeof fw.percent === "number" ? statusPill(fw.status) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Key Metrics */}
      <div className="rounded-2xl border border-[color:var(--color-border)] bg-gradient-to-br from-[color:var(--color-surface-sunken)] to-[color:var(--color-bg-white)] p-6 shadow-sm">
        <h4 className="text-sm font-bold text-[color:var(--color-text-hero)] uppercase tracking-wide mb-4">Key Metrics</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard
            label="Mean Time to Detect (MTTD)"
            value={keyMetrics.mttdHours != null ? keyMetrics.mttdHours.toFixed(1) : null}
            unit="hrs"
          />
          <MetricCard
            label="Mean Time to Respond (MTTR)"
            value={keyMetrics.mttrHours != null ? keyMetrics.mttrHours.toFixed(1) : null}
            unit="hrs"
          />
          <MetricCard
            label="Open Risk Items"
            value={keyMetrics.openRiskItems}
          />
          <MetricCard
            label="Evidence Coverage"
            value={keyMetrics.evidenceCoverage}
            unit="%"
            color={keyMetrics.evidenceCoverage == null ? undefined : keyMetrics.evidenceCoverage >= 80 ? "green" : keyMetrics.evidenceCoverage >= 50 ? "amber" : "red"}
          />
          <MetricCard
            label="Overdue Corrective Actions"
            value={keyMetrics.overdueActions}
            color={keyMetrics.overdueActions == null ? undefined : keyMetrics.overdueActions > 3 ? "red" : keyMetrics.overdueActions > 0 ? "amber" : "green"}
          />
          <MetricCard
            label="Audit Readiness Score"
            value={auditUnavailable ? null : `${keyMetrics.auditReadinessScore}`}
            unit="%"
            unavailableReason={auditUnavailable ? (typeof auditReadiness.reason === "string" ? auditReadiness.reason : "") : undefined}
            color={auditUnavailable ? undefined : (keyMetrics.auditReadinessScore >= 75 ? "green" : keyMetrics.auditReadinessScore >= 50 ? "amber" : "red")}
            badge={!auditUnavailable && auditReadiness.availability === "unvalidated_estimate" ? <Tag className="text-[10px]">Unvalidated estimate</Tag> : null}
            note={!auditUnavailable && auditReadiness.availability === "unvalidated_estimate" ? (/client/i.test(auditReadiness.reason || "") ? auditReadiness.reason : `${auditReadiness.reason ? auditReadiness.reason + " " : ""}Do not share with the client.`) : null}
          />
        </div>
      </div>
    </div>
  );
}

function EvidenceDocDrawer({ row, open, onClose, projectId, companyDisplayName, getApiToken, readOnly, onSaveDraft }) {
  if (!row) return null;
  const isExternal = row.category === "external";
  const titleDisplay = isExternal
    ? (row.title || "Untitled")
    : (stripCompanyPrefixFromDocumentTitle(row.title, companyDisplayName) || "Untitled");
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
      title={
        <div>
          <div className="flex items-center gap-2">
            <FileTextOutlined className="text-[color:var(--color-accent)]" />
            <span className="text-lg font-bold text-[color:var(--color-text-hero)]">{titleDisplay}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[color:var(--color-text-muted)]">
            <span className="font-mono">{row.evidenceId}</span>
            <span>Control: <span className="font-bold text-[color:var(--color-accent)]">{row.controlRef}</span></span>
            <Tag color={isExternal ? "purple" : "blue"} className="text-[10px]">{isExternal ? "External" : "Platform"}</Tag>
            <Tag color="blue" className="text-[10px]">v{row.version}</Tag>
            <span>{row.versionDate ? formatDdMmYyyy(row.versionDate) : ""}</span>
          </div>
        </div>
      }
    >
      <div className="mt-4">
        {isExternal ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4 grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-[color:var(--color-text-muted)]">Status</span><div className="font-semibold">{row.status}</div></div>
              <div><span className="text-[color:var(--color-text-muted)]">Owner</span><div className="font-semibold">{row.owner}</div></div>
            </div>
            {row.externalFileUrl && (
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => window.open(row.externalFileUrl, "_blank", "noopener,noreferrer")}
              >
                Open file link
              </Button>
            )}
          </div>
        ) : null}
        {!isExternal && row.aiScoringFailed && (
          <div data-testid="ai-scoring-failed-panel" className="mb-6 rounded-xl border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] p-4 text-sm text-[color:var(--color-danger-fg)]">
            <strong>Scoring failed - retry.</strong> The latest AI scoring attempt for this document did not complete, so no score is recorded for it
            {row.aiScoringError ? ` (${row.aiScoringError})` : ""}. Run "Score with AI" again.
            {row.aiLastGoodScore && !row.aiScore ? ` An earlier score of ${row.aiLastGoodScore.overallScore}% exists but is out of date and is not counted.` : ""}
          </div>
        )}
        {!isExternal && row.aiScore && (
          <div className="mb-6 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-[color:var(--color-text-hero)]">AI evaluation</h4>
              {row.aiScoredAt && (
                <span className="text-[11px] text-[color:var(--color-text-muted)]">
                  Scored {formatDdMmYyyyTime(row.aiScoredAt)}
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl ${aiScoreColorClass(row.aiOverallScore)}`}>
                {row.aiOverallScore}%
              </span>
              <span className="text-xs text-[color:var(--color-text-muted)]">overall quality</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <AiDimensionBar label="Control alignment" value={row.aiScore.controlAlignment} />
              <AiDimensionBar label="Completeness" value={row.aiScore.completeness} />
              <AiDimensionBar label="Clarity" value={row.aiScore.clarity} />
              <AiDimensionBar label="Audit readiness" value={row.aiScore.auditReadiness} />
            </div>
            {row.aiScore.summary && (
              <p className="text-sm text-[color:var(--color-text-hero)]">{row.aiScore.summary}</p>
            )}
            {row.aiScore.strengths?.length > 0 && (
              <div>
                <div className="text-xs font-bold text-[color:var(--color-ok-fg)] mb-1">Strengths</div>
                <ul className="list-disc pl-4 text-sm text-[color:var(--color-text-hero)] space-y-0.5">
                  {row.aiScore.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {row.aiScore.gaps?.length > 0 && (
              <div>
                <div className="text-xs font-bold text-[color:var(--color-warn-fg)] mb-1">Gaps</div>
                <ul className="list-disc pl-4 text-sm text-[color:var(--color-text-hero)] space-y-0.5">
                  {row.aiScore.gaps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {row.aiScore.improvements?.length > 0 && (
              <div>
                <div className="text-xs font-bold text-[color:var(--color-accent)] mb-1">Improvements</div>
                <ol className="list-decimal pl-4 text-sm text-[color:var(--color-text-hero)] space-y-0.5">
                  {row.aiScore.improvements.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
          </div>
        )}
        {!isExternal && row.brandedBlobUrl ? (
          <CiDocxEditorPanel
            projectId={projectId}
            controlId={row.controlId}
            brandedBlobUrl={row.brandedBlobUrl}
            draftHtml={row.draftHtml || ""}
            readOnly={readOnly}
            getApiToken={getApiToken}
            onSaveDraft={onSaveDraft}
            onRefresh={() => {}}
          />
        ) : !isExternal ? (
          <div className="flex flex-col items-center justify-center py-12 text-[color:var(--color-text-muted)]">
            <FileTextOutlined className="text-3xl mb-2" />
            <p className="text-sm">No branded document available for this control.</p>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export default EvidencingMonitoring;
